import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { restApi } from "~/redux/restApi";
import { useGetOnboardingStatusQuery } from "~/redux/api/onboarding";
import {
  Shield,
  Layers,
  Handshake,
  Briefcase,
  LayoutGrid,
  Contact,
  Activity,
  Coins,
} from "lucide-react";
import { cn } from "~/lib/utils";

interface ModuleItem {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  path: string;
}

const showDailyOps = import.meta.env.DEV;

const coreModules: ModuleItem[] = [
  { id: "hse", name: "HSE", icon: Shield, path: "/" },
  { id: "assets", name: "Assets", icon: Layers, path: "/asset/assets" },
  {
    id: "contractors",
    name: "Contractors",
    icon: Handshake,
    path: "/contractors",
  },
  { id: "resources", name: "Resources", icon: Briefcase, path: "/resources" },
  {
    id: "personnel",
    name: "Personnel",
    icon: Contact,
    path: "/personnel",
  },
  {
    id: "finance",
    name: "Finance",
    icon: Coins,
    path: "/finance",
  },
];

const dailyOpsModule: ModuleItem = {
  id: "daily-ops",
  name: "Daily Ops",
  icon: Activity,
  path: "/daily-ops",
};

const springTransition = {
  type: "spring",
  stiffness: 110,
  damping: 22,
  mass: 1.0,
} as const;

const closeSpringTransition = {
  type: "spring",
  stiffness: 40,
  damping: 20,
  mass: 1.0,
} as const;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.02,
      delayChildren: 0.05,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.15,
      ease: "easeOut",
    },
  },
} as const;

const itemVariants = {
  hidden: { y: 8, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 130,
      damping: 18,
      mass: 0.9,
    } as const,
  },
};

const getActiveModule = (pathname: string) => {
  if (pathname.startsWith("/hse")) return "hse";
  if (pathname.startsWith("/daily-ops")) return "daily-ops";
  if (pathname.startsWith("/finance")) return "finance";
  if (pathname.startsWith("/asset")) return "assets";
  if (pathname.startsWith("/contractors")) return "contractors";
  if (
    pathname.startsWith("/resources") ||
    pathname.startsWith("/resource/inventory")
  )
    return "resources";
  if (
    pathname.startsWith("/personnel") ||
    pathname.startsWith("/resource/employees")
  )
    return "personnel";
  return "hse";
};

export function EcosystemDock() {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dockRef = useRef<HTMLDivElement>(null);
  const [selectedModule, setSelectedModule] = useState(() =>
    getActiveModule(location.pathname),
  );
  const closeTimeoutRef = useRef<number | null>(null);
  const [showCollapsedContent, setShowCollapsedContent] = useState(true);

  const { data: onboardingStatus } = useGetOnboardingStatusQuery();
  const { data: myModuleAccess } = restApi.useGetMyModuleAccessQuery();

  const isModuleVisible = (moduleName: string) => {
    if (!onboardingStatus || !myModuleAccess || !Array.isArray(myModuleAccess))
      return true;

    const isLocked = onboardingStatus.lockedModules?.some(
      (m) => m?.toLowerCase() === moduleName?.toLowerCase(),
    );
    if (isLocked) return false;

    const matchedAccess = myModuleAccess.find(
      (ma) => ma?.moduleKey?.toLowerCase() === moduleName?.toLowerCase(),
    );
    if (matchedAccess && !matchedAccess.isEnabled) return false;

    return true;
  };

  useEffect(() => {
    if (isExpanded) {
      setShowCollapsedContent(false);
    }
  }, [isExpanded]);

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    if (isExpanded) {
      closeTimeoutRef.current = window.setTimeout(() => {
        setIsExpanded(false);
        closeTimeoutRef.current = null;
      }, 1200); // 1.2s delay to collapse back slowly, giving room to re-hover to cancel
    }
  };

  useEffect(() => {
    const active = getActiveModule(location.pathname);
    const isHseHome =
      location.pathname === "/" ||
      location.pathname === "/home" ||
      location.pathname === "/dashboard";

    if (active !== "hse" || isHseHome) {
      setSelectedModule(active);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dockRef.current && !dockRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
      if (closeTimeoutRef.current !== null) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [isExpanded]);

  return (
    <motion.div
      layout
      transition={isExpanded ? springTransition : closeSpringTransition}
      onLayoutAnimationComplete={() => {
        if (!isExpanded) {
          setShowCollapsedContent(true);
        }
      }}
      className={cn(
        "ecosystem-dock pointer-events-none",
        isExpanded
          ? "is-expanded rounded-t-[24px] rounded-b-none p-2 pt-3 pb-2"
          : "rounded-t-xl rounded-b-none cursor-pointer",
      )}
      style={{
        width: isExpanded ? "calc(100% - 32px)" : "110px",
        height: isExpanded ? "auto" : "32px",
        maxWidth: isExpanded
          ? showDailyOps && isModuleVisible("Daily Ops")
            ? "780px"
            : "670px"
          : "110px",
      }}
      onClick={() => {
        if (!isExpanded) setIsExpanded(true);
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={dockRef}
      aria-expanded={isExpanded}
      role={isExpanded ? "navigation" : "button"}
      aria-label={
        isExpanded ? "Modules Switcher Menu" : "Expand Modules Switcher"
      }
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          if (!isExpanded) {
            setIsExpanded(true);
            e.preventDefault();
          }
        }
      }}
    >
      <div className="ecosystem-dock-content w-full h-full flex items-center justify-center pointer-events-auto">
        <AnimatePresence>
          {isExpanded ? (
            <motion.div
              key="expanded"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={cn(
                "w-full p-1 flex items-center gap-2",
                showDailyOps && isModuleVisible("Daily Ops")
                  ? "justify-start"
                  : "justify-between",
              )}
            >
              {coreModules
                .filter((mod) => isModuleVisible(mod.name))
                .map((mod) => {
                  const Icon = mod.icon;
                  const isActive = selectedModule === mod.id;
                  return (
                    <motion.button
                      key={mod.id}
                      variants={itemVariants}
                      whileTap={{ scale: 0.96 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (closeTimeoutRef.current !== null) {
                          window.clearTimeout(closeTimeoutRef.current);
                          closeTimeoutRef.current = null;
                        }
                        setSelectedModule(mod.id);
                        navigate(mod.path);
                        setIsExpanded(false);
                      }}
                      className={cn(
                        "module-item flex-1 relative overflow-hidden transition-colors duration-200 z-10",
                        isActive
                          ? "is-active text-[#000000] font-Bold"
                          : "text-white/80 hover:text-white font-Regular",
                      )}
                      aria-current={isActive ? "page" : undefined}
                      aria-label={`Switch to ${mod.name}`}
                      tabIndex={0}
                    >
                      {isActive && (
                        <motion.div
                          layoutId="active-indicator"
                          className="absolute inset-0 bg-[#CBFF10] z-0"
                          style={{
                            borderRadius: "inherit",
                          }}
                          transition={springTransition}
                        />
                      )}
                      <Icon className="w-5 h-5 shrink-0 relative z-10" />
                      <span className="text-[11px] tracking-wider uppercase select-none font-inherit relative z-10">
                        {mod.name}
                      </span>
                    </motion.button>
                  );
                })}

              {showDailyOps && isModuleVisible("Daily Ops") && (
                <>
                  <div className="w-[1px] h-8 bg-white/20 self-center shrink-0" />
                  <motion.button
                    key={dailyOpsModule.id}
                    variants={itemVariants}
                    whileTap={{ scale: 0.96 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (closeTimeoutRef.current !== null) {
                        window.clearTimeout(closeTimeoutRef.current);
                        closeTimeoutRef.current = null;
                      }
                      setSelectedModule(dailyOpsModule.id);
                      navigate(dailyOpsModule.path);
                      setIsExpanded(false);
                    }}
                    className={cn(
                      "module-item flex-1 relative overflow-hidden transition-colors duration-200 z-10",
                      selectedModule === dailyOpsModule.id
                        ? "is-active text-[#000000] font-Bold"
                        : "text-white/80 hover:text-white font-Regular",
                    )}
                    aria-current={
                      selectedModule === dailyOpsModule.id ? "page" : undefined
                    }
                    aria-label={`Switch to ${dailyOpsModule.name}`}
                    tabIndex={0}
                  >
                    {selectedModule === dailyOpsModule.id && (
                      <motion.div
                        layoutId="active-indicator"
                        className="absolute inset-0 bg-[#CBFF10] z-0"
                        style={{
                          borderRadius: "inherit",
                        }}
                        transition={springTransition}
                      />
                    )}
                    <Activity className="w-5 h-5 shrink-0 relative z-10" />
                    <span className="text-[11px] tracking-wider uppercase select-none font-inherit relative z-10">
                      {dailyOpsModule.name}
                    </span>
                  </motion.button>
                </>
              )}
            </motion.div>
          ) : showCollapsedContent ? (
            <motion.div
              key="collapsed"
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{
                opacity: 0,
                y: 6,
                scale: 0.95,
                transition: { duration: 0.08 },
              }}
              transition={{
                type: "spring",
                stiffness: 220,
                damping: 20,
              }}
              className="flex items-center justify-center gap-2 select-none absolute inset-0 pt-[2px]"
            >
              <LayoutGrid className="w-4 h-4 text-[#CBFF10] shrink-0" />
              <span className="text-xs font-Bold text-white tracking-wide whitespace-nowrap">
                Modules
              </span>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
